import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { getAssociationsQuery, getAssociationsNameInZoneQuery } from '../../queries/queries';
import { GET_BOOKLETS_WITH_SERIES, DELETE_BOOKLET_SERIES } from '../../queries/bookletSeries';
import { LoadingTable, EditButton, TableSection, TableHeader } from '../../common/index';
import Form from './BookletsForm';
import BookletSeriesForm from './BookletSeriesForm';
import BookletSeriesRow from './BookletSeriesRow';
import MoveToSeriesModal from './MoveToSeriesModal';
import CloneBookletModal from './CloneBookletModal';
import SalesView from './BookletsSalesView';
import Modal from '../../common/Modal';
import { useConfirmationModalContext } from '../../context/ConfirmationModalContext';
import Tooltip from '../../common/Tooltip';

const { REACT_APP_IMAGE_BASE_URL } = process.env;

function BookletsTable({ zoneId }) {

  const confirmationModalContext = useConfirmationModalContext();

  const { data, loading, error } = useQuery(GET_BOOKLETS_WITH_SERIES, { variables: { zoneId } });
  const { data: associationsData } = useQuery(getAssociationsQuery);
  const { data: zoneAssociationsData } = useQuery(getAssociationsNameInZoneQuery, { variables: { zoneId } });
  const [deleteBookletSeries] = useMutation(DELETE_BOOKLET_SERIES, {
    refetchQueries: [{ query: GET_BOOKLETS_WITH_SERIES, variables: { zoneId } }],
    onError: (err) => {
      confirmationModalContext.showConfirmation('Fel', err.message);
    },
  });

  const [editingBooklet, setEditingBooklet] = useState(null);
  const [editingSeries, setEditingSeries] = useState('');
  const [viewingSales, setViewingSales] = useState('');
  const [movingToSeries, setMovingToSeries] = useState(null);
  const [addingBookletInSeries, setAddingBookletInSeries] = useState(null);
  const [cloningBooklet, setCloningBooklet] = useState(null);
  const [sortBy, setSortBy] = useState('title');
  const [sortOrder, setSortOrder] = useState(1);
  const sort = { sortBy, sortOrder, setSortBy, setSortOrder };

  const { bookletSeriesInZone = [] } = data || {};

  const isDateActive = (startDate, endDate) => {
    const today = new Date();
    return today >= new Date(startDate) && today <= new Date(endDate);
  };

  const handleConfirmEdit = async (bookletId) => {
    let selectedBooklet = null;
    for (const series of bookletSeriesInZone) {
      selectedBooklet = series.booklets.find(booklet => booklet._id === bookletId);
      if (selectedBooklet) break;
    }

    if (!selectedBooklet) {
      setEditingBooklet(null);
      return;
    }

    const isBookletActive = isDateActive(selectedBooklet.buyableFrom, selectedBooklet.buyableTo);

    if (!isBookletActive) {
      setEditingBooklet(selectedBooklet);
      return;
    }

    const result = await confirmationModalContext.showConfirmation('Häftet är aktivt!', 'Är du säker på att du vill redigera det aktiva häftet?');

    if (!result) {
      setEditingBooklet(null);
      return;
    }

    setEditingBooklet(selectedBooklet);
  };

  const addMonths = (dateStr, months) => {
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + months);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  const handleAddBookletInSeries = (seriesId) => {
    const series = bookletSeriesInZone.find((s) => s._id === seriesId);
    if (!series) return;

    const defaults = {
      title: series.name,
      price: series.price,
      mainAssociationId: series.mainAssociationId,
      purchasableThroughAssociations: series.purchasableThroughAssociations,
      totalCommission: series.totalCommission,
      supportCommissionToAssociation: series.supportCommissionToAssociation,
      supportCommissionToMainAssociation: series.supportCommissionToMainAssociation,
      colorLeft: series.colorLeft,
      colorRight: series.colorRight,
      colorFont: series.colorFont,
      colorSalesMaterial: series.colorSalesMaterial,
      logoURL: series.logoURL,
      adminEmails: series.adminEmails,
      bookletSeriesId: series._id,
      defaultValidityMonths: series.defaultValidityMonths,
    };

    // Auto-fill dates from last booklet in series
    if (series.booklets && series.booklets.length > 0) {
      const sorted = [...series.booklets].sort((a, b) => new Date(b.validTo || 0) - new Date(a.validTo || 0));
      const lastBooklet = sorted[0];
      const nextDay = new Date(lastBooklet.validTo);
      nextDay.setDate(nextDay.getDate() + 1);
      const startDate = nextDay.toISOString().slice(0, 10);

      defaults.validFrom = startDate;
      defaults.buyableFrom = startDate;

      if (series.defaultValidityMonths) {
        defaults.validTo = addMonths(startDate, series.defaultValidityMonths);
        defaults.buyableTo = addMonths(startDate, series.defaultValidityMonths);
      }
    }

    setAddingBookletInSeries(defaults);
  };

  const handleCloneBooklet = (booklet) => {
    const series = bookletSeriesInZone.find((s) => s._id === booklet.bookletSeriesId);
    if (!series) return;
    setCloningBooklet({ booklet, series });
  };

  const handleDeleteSeries = async (seriesId) => {
    const result = await confirmationModalContext.showConfirmation(
      'Ta bort serie',
      'Är du säker på att du vill ta bort denna serie?'
    );
    if (!result) return;
    deleteBookletSeries({ variables: { _id: seriesId } });
  };

  // Reusable function to render a booklet row
  const renderBookletRow = (obj, options = {}) => {
    const { isNested = false, tag = null, overlaps = null } = options;

    if (viewingSales === obj._id) {
      return (
        <SalesView
          key={`sales-${obj._id}`}
          booklet={obj}
          zoneId={zoneId}
          onClose={() => setViewingSales('')}
        />
      );
    }

    const isEditing = editingBooklet && editingBooklet._id === obj._id;
    const isActive = isDateActive(obj.buyableFrom, obj.buyableTo);
    const isExpired = new Date() > new Date(obj.validTo);
    const rowStyle = isEditing
      ? { backgroundColor: '#e8f6f6', borderLeft: '4px solid #82C8C8' }
      : isActive
        ? { backgroundColor: '#FFE4EB' }
        : isExpired
          ? { opacity: 0.25 }
          : {};

    return (
      <tr key={obj._id} className={isNested ? 'nested-booklet' : ''} style={rowStyle}>
        <td className={isNested ? 'nested-indent' : ''}>
          {tag && (
            <Tooltip content={tag === 'NU'
              ? 'Detta häfte är giltigt just nu (giltighetsperioden har börjat men inte slutat)'
              : 'Detta häfte blir nästa giltiga i serien (giltighetsperioden har inte börjat ännu)'
            }>
              <span className={`booklet-tag booklet-tag--${tag.toLowerCase()}`}>
                {tag}
              </span>
            </Tooltip>
          )}
        </td>
        <td className="centered-logo">
          {obj.logoURL ? (
            <img
              alt="logo"
              src={`${REACT_APP_IMAGE_BASE_URL}${obj.logoURL}`}
              style={{ maxWidth: '100px', maxHeight: '60px' }}
            />
          ) : ''}
        </td>
        <td>
          {obj.title}<br />
          {obj.subtitle && <small>{obj.subtitle}</small>}
        </td>
        <td className={overlaps?.sales ? 'overlap-warning' : ''}>
          {new Date(`${obj.buyableFrom}`).toLocaleDateString()} - {new Date(`${obj.buyableTo}`).toLocaleDateString()}
          {overlaps?.sales && <Tooltip content="Säljperioden överlappar med ett annat häfte i serien"><span className="overlap-icon">&#9888;</span></Tooltip>}
        </td>
        <td className={overlaps?.valid ? 'overlap-warning' : ''}>
          {new Date(`${obj.validFrom}`).toLocaleDateString()} - {new Date(`${obj.validTo}`).toLocaleDateString()}
          {overlaps?.valid && <Tooltip content="Giltighetsperioden överlappar med ett annat häfte i serien"><span className="overlap-icon">&#9888;</span></Tooltip>}
        </td>
        <td>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, width: 'fit-content' }}>
            {obj.colorLeft && <div title="Färg vänster" style={{ backgroundColor: obj.colorLeft, width: 20, height: 20, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }}></div>}
            {obj.colorRight && <div title="Färg höger" style={{ backgroundColor: obj.colorRight, width: 20, height: 20, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }}></div>}
            {obj.colorFont && <div title="Färg text" style={{ backgroundColor: obj.colorFont, width: 20, height: 20, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }}></div>}
            {obj.colorSalesMaterial && <div title="Färg försäljningsmaterial" style={{ backgroundColor: obj.colorSalesMaterial, width: 20, height: 20, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }}></div>}
          </div>
        </td>
        <td>{obj.price}</td>
        <td className="booklet-sold">{obj.usedOrders}/{obj.soldBooklets}</td>
        <td>
          <EditButton size="xs" setEditing={() => handleConfirmEdit(obj._id)}>Redigera</EditButton>
          <button className="button secondary xs" onClick={() => handleCloneBooklet(obj)}>Klona</button>
          <button className="button secondary xs" onClick={() => setViewingSales(obj._id)}>Försäljning</button>
          <button className="button secondary xs" onClick={() => setMovingToSeries(obj)}>
            Byt serie
          </button>
          <Link className="button secondary xs arrow" to={`/cities/${obj._id}`}>Preview</Link>
        </td>
      </tr>
    );
  };

  const bookletColumns = [
    { label: '', style: { width: '10px' } },
    { label: 'Logga', className: 'centered-logo' },
    { label: 'Häfte', sortField: 'title' },
    { label: 'Säljperiod', sortField: 'buyableFrom' },
    { label: 'Giltig', sortField: 'validFrom' },
    { label: 'Färger' },
    { label: 'Pris (kr)' },
    { label: 'Akt./Sålda', className: 'booklet-sold' },
    { label: '' },
  ];

  const bookletColgroup = (
    <colgroup>
      <col style={{ width: 10 }} />
      <col style={{ width: 100 }} />
      <col />
      <col style={{ width: 150 }} />
      <col style={{ width: 150 }} />
      <col style={{ width: 80 }} />
      <col style={{ width: 70 }} />
      <col style={{ width: 100 }} />
      <col />
    </colgroup>
  );

  const renderTableHeader = () => (
    <TableHeader columns={bookletColumns} sort={sort} />
  );

  return (
    <TableSection>
      <div className="table-header">
        <h2>Häften</h2>
        <EditButton size="sm add" setEditing={() => setEditingSeries('new')}>Lägg till serie</EditButton>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.8rem', color: 'rgba(0,0,0,0.5)', marginLeft: 'auto' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#FFE4EB', border: '1px solid rgba(0,0,0,0.1)' }}></span>
            Aktivt
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#fff', border: '1px solid rgba(0,0,0,0.1)' }}></span>
            Kommande
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: 0.25 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#fff', border: '1px solid rgba(0,0,0,0.1)' }}></span>
            Utgånget
          </span>
        </div>
      </div>

      {/* Loading/error state */}
      {!data ? (
        <table className="booklet-table">
          {renderTableHeader()}
          <tbody>
            <LoadingTable loading={loading} error={error} />
          </tbody>
        </table>
      ) : (
        <>
          {/* Warning if any series is missing a next booklet */}
          {bookletSeriesInZone.some((s) => s.booklets.length > 0 && !s.nextBooklet) && (
            <div className="booklet-series-warning">
              {bookletSeriesInZone
                .filter((s) => s.booklets.length > 0 && !s.nextBooklet)
                .map((s) => (
                  <div key={s._id}>
                    <strong>{s.name}</strong> saknar ett kommande häfte. <br /> 
                    Lägg till ett häfte så att användare i appen kan se vilket som är det nästkommande häftet.
                  </div>
                ))
              }
            </div>
          )}

          {/* Each series gets its own table card */}
          {bookletSeriesInZone.map((series) => (
            <table className="booklet-table" key={series._id} style={{ padding: 0, overflow: 'hidden', marginTop: '2rem' }}>
              {bookletColgroup}
              <tbody>
                <BookletSeriesRow
                  series={series}
                  onEditSeries={setEditingSeries}
                  onAddBooklet={handleAddBookletInSeries}
                  onDeleteSeries={handleDeleteSeries}
                  renderBookletRow={renderBookletRow}
                />
              </tbody>
            </table>
          ))}



        </>
      )}

      {/* Series form modals */}
      {editingSeries === 'new' && (
        <Modal isOpen closeModal={() => setEditingSeries('')} className="modal-booklet-form" showClose={false}>
          <h2>Skapar Serie</h2>
          <BookletSeriesForm
            key="new-series"
            onComplete={() => setEditingSeries('')}
            zoneId={zoneId}
            associations={associationsData?.associations}
            zoneAssociations={zoneAssociationsData?.associations}
          />
        </Modal>
      )}

      {editingSeries && editingSeries !== 'new' && (() => {
        const series = bookletSeriesInZone.find((s) => s._id === editingSeries);
        return series ? (
          <Modal isOpen closeModal={() => setEditingSeries('')} className="modal-booklet-form" showClose={false}>
            <h2>Redigerar Serie</h2>
            <BookletSeriesForm
              content={series}
              onComplete={() => setEditingSeries('')}
              zoneId={zoneId}
              associations={associationsData?.associations}
              zoneAssociations={zoneAssociationsData?.associations}
            />
          </Modal>
        ) : null;
      })()}

      {/* Booklet edit modal */}
      {editingBooklet && (
        <Modal isOpen closeModal={() => setEditingBooklet(null)} className="modal-booklet-form" showClose={false}>
          <h2>Redigerar Häfte</h2>
          <Form
            content={editingBooklet}
            onComplete={() => setEditingBooklet(null)}
            associations={associationsData?.associations}
            zoneAssociations={zoneAssociationsData?.associations}
            zoneId={zoneId}
            refetchQuery={GET_BOOKLETS_WITH_SERIES}
          />
        </Modal>
      )}

      {/* Booklet create (in series) modal */}
      {addingBookletInSeries && (
        <Modal isOpen closeModal={() => setAddingBookletInSeries(null)} className="modal-booklet-form" showClose={false}>
          <h2>Skapar Häfte</h2>
          <Form
            key="new-in-series"
            content={addingBookletInSeries}
            onComplete={() => setAddingBookletInSeries(null)}
            associations={associationsData?.associations}
            zoneAssociations={zoneAssociationsData?.associations}
            zoneId={zoneId}
            refetchQuery={GET_BOOKLETS_WITH_SERIES}
          />
        </Modal>
      )}

      {/* Clone booklet modal */}
      {cloningBooklet && (
        <CloneBookletModal
          booklet={cloningBooklet.booklet}
          series={cloningBooklet.series}
          zoneId={zoneId}
          onClose={() => setCloningBooklet(null)}
        />
      )}

      {/* Move to Series Modal */}
      {movingToSeries && (
        <MoveToSeriesModal
          booklet={movingToSeries}
          series={bookletSeriesInZone}
          zoneId={zoneId}
          onClose={() => setMovingToSeries(null)}
        />
      )}
    </TableSection>
  );
}

BookletsTable.propTypes = {
  zoneId: PropTypes.string.isRequired
};

export default BookletsTable;
